import { lazy, ComponentType } from 'react';
import { useRoutes } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { canSeeTalentBank } from './services/talentService';

function lazyWithRetry<T extends ComponentType<unknown>>(
    componentImport: () => Promise<{ default: T }>
) {
    return lazy(async () => {
        const pageHasAlreadyBeenForceRefreshed = JSON.parse(
            window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
        );

        try {
            const component = await componentImport();
            window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');
            return component;
        } catch (error) {
            if (!pageHasAlreadyBeenForceRefreshed) {
                window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
                window.location.reload();
                // Retorna uma promise que nunca resolve enquanto a página recarrega
                return new Promise<{ default: T }>(() => {});
            }
            throw error;
        }
    });
}

const Dashboard = lazyWithRetry(() => import('./components/Dashboard').then(m => ({ default: m.Dashboard })));
const Sales = lazyWithRetry(() => import('./components/Sales').then(m => ({ default: m.Sales })));
const LeadsPage = lazyWithRetry(() => import('./pages/LeadsPage').then(m => ({ default: m.LeadsPage })));
const TurmasPage = lazyWithRetry(() => import('./pages/TurmasPage').then(m => ({ default: m.TurmasPage })));
const EventCostsPage = lazyWithRetry(() => import('./pages/EventCostsPage').then(m => ({ default: m.EventCostsPage })));
const AlunosPage = lazyWithRetry(() => import('./pages/AlunosPage').then(m => ({ default: m.AlunosPage })));
const CalendarPage = lazyWithRetry(() => import('./pages/CalendarPage').then(m => ({ default: m.CalendarPage })));
const Users = lazyWithRetry(() => import('./components/Users').then(m => ({ default: m.Users })));
const ProducaoDashboard = lazyWithRetry(() => import('./components/dashboards/ProducaoDashboard').then(m => ({ default: m.ProducaoDashboard })));
const Inventory = lazyWithRetry(() => import('./components/Inventory').then(m => ({ default: m.Inventory })));
const SettingsPage = lazyWithRetry(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));

const Blog = lazyWithRetry(() => import('./components/Blog').then(m => ({ default: m.Blog })));
const Chat = lazyWithRetry(() => import('./components/Chat').then(m => ({ default: m.Chat })));
const Profile = lazyWithRetry(() => import('./components/Profile').then(m => ({ default: m.Profile })));
const SuperAdminPage = lazyWithRetry(() => import('./pages/superadmin/SuperAdminPage').then(m => ({ default: m.SuperAdminPage })));

const EstimatesPage = lazyWithRetry(() => import('./pages/EstimatesPage').then(m => ({ default: m.EstimatesPage })));
const ChatInboxPage = lazyWithRetry(() => import('./pages/ChatInboxPage').then(m => ({ default: m.ChatInboxPage })));
const FinanceDashboard = lazyWithRetry(() => import('./components/dashboards/FinanceDashboard').then(m => ({ default: m.FinanceDashboard })));
const TrainingPage = lazyWithRetry(() => import('./pages/TrainingPage').then(m => ({ default: m.TrainingPage })));
const AiAgentPage = lazyWithRetry(() => import('./pages/AiAgentPage').then(m => ({ default: m.AiAgentPage })));
const TalentBankPage = lazyWithRetry(() => import('./pages/TalentBankPage').then(m => ({ default: m.TalentBankPage })));
const AcmGeneratorPage = lazyWithRetry(() => import('./pages/AcmGeneratorPage').then(m => ({ default: m.AcmGeneratorPage })));

export const AppRoutes = () => {
    return useRoutes([
        {
            path: '/',
            element: <Dashboard />,
        },
        {
            path: '/dashboard',
            element: <Dashboard />,
        },
        {
            path: '/turmas',
            element: (
                <ProtectedRoute requiredPermission="turmas.access" niche="cursos" module="turmas">
                    <TurmasPage />
                </ProtectedRoute>
            ),
        },
        {
            path: '/eventos',
            element: (
                <ProtectedRoute requiredPermission="finance.access" niche="cursos">
                    <EventCostsPage />
                </ProtectedRoute>
            ),
        },
        {
            path: '/alunos',
            element: (
                <ProtectedRoute requiredPermission="alunos.access" niche="cursos">
                    <AlunosPage />
                </ProtectedRoute>
            ),
        },
        {
            path: '/sales',
            element: (
                <ProtectedRoute requiredPermission="sales_dashboard.access">
                    <Sales />
                </ProtectedRoute>
            ),
        },
        {
            path: '/leads',
            element: <LeadsPage />,
        },
        {
            path: '/treinamento',
            element: <TrainingPage />,
        },
        {
            path: '/calendar',
            element: <CalendarPage />,
        },
        {
            path: '/users',
            element: (
                <ProtectedRoute requiredPermission="users.manage">
                    <Users />
                </ProtectedRoute>
            ),
        },
        {
            path: '/producao',
            element: (
                <ProtectedRoute requiredPermission="producao.access" niche="acm">
                    <ProducaoDashboard />
                </ProtectedRoute>
            ),
        },
        {
            path: '/inventory',
            element: (
                <ProtectedRoute requiredPermission="inventory.access">
                    <Inventory />
                </ProtectedRoute>
            ),
        },
        {
            path: '/settings',
            element: (
                <ProtectedRoute requiredPermission="settings.access">
                    <SettingsPage />
                </ProtectedRoute>
            ),
        },
        {
            path: '/inbox',
            element: (
                <ProtectedRoute requiredPermission="inbox.access">
                    <ChatInboxPage />
                </ProtectedRoute>
            ),
        },
        {
            path: '/finance',
            element: (
                <ProtectedRoute requiredPermission="finance.access">
                    <FinanceDashboard />
                </ProtectedRoute>
            ),
        },

        {
            path: '/blog',
            element: (
                <ProtectedRoute requiredPermission="blog.access">
                    <Blog />
                </ProtectedRoute>
            ),
        },
        {
            path: '/chat',
            element: <Chat />,
        },
        {
            path: '/profile',
            element: <Profile />,
        },
        {
            path: '/superadmin',
            element: (
                <ProtectedRoute requiredPermission="superadmin.access">
                    <SuperAdminPage />
                </ProtectedRoute>
            ),
        },
        {
            path: '/estimates',
            element: (
                <ProtectedRoute requiredPermission="estimates.access" niche="acm">
                    <EstimatesPage />
                </ProtectedRoute>
            ),
        },
        {
            path: '/atendente-ia',
            element: <AiAgentPage />,
        },
        {
            path: '/banco-talentos',
            element: (
                <ProtectedRoute requiredPermission="talent.access" companyGate={canSeeTalentBank}>
                    <TalentBankPage />
                </ProtectedRoute>
            ),
        },
        {
            path: '/projetos-acm',
            element: (
                <ProtectedRoute requiredPermission="acm_generator.access" niche="acm">
                    <AcmGeneratorPage />
                </ProtectedRoute>
            ),
        },
    ]);
};
